import { useCallback } from "react";
import { ContentBlock, ContentBlockShape, ContentBlockType } from "@/models";
import { useAppDispatch, useAppSelector } from "@/redux-state";
import {
    gotContentBlock,
    gotContentBlocks,
    // gotTopLevelContentBlockLayers,
} from "@/redux-state/contentBlockDataSlice";
import { useContentBlockState } from "@/Pages/DesignStudio/Design/useContentBlockState";

export const useFindContentBlockMethods = () => {
    const {contentBlocks: _contentBlocks} = useContentBlockState();

    const rootContentBlocks = useAppSelector((state) =>
        Object.values(state.contentBlockStore.nestedContentBlocks).filter(
            (contentBlock) => !contentBlock.parent_content_block_id,
        ),
    );

    const getContentBlockWithChildren = useCallback(
        (
            contentBlockId:string,
        ): ContentBlockShape | null => {
            const contentBlock = Object.values(_contentBlocks).find(
                (block) => block.id === contentBlockId,
            );
            if (!contentBlock) return null;
            const childBlocks = Object.values(_contentBlocks)
                .filter(
                    (block) =>
                        block.parent_content_block_id === contentBlock.id,
                )
                .sort((a, b) => a.weight - b.weight);

            if (!childBlocks?.length) {
                if (contentBlock?.contentBlocks?.length) {
                    return {
                        ...contentBlock,
                        contentBlocks: contentBlock.contentBlocks.sort(
                            (a, b) => a.weight - b.weight,
                        ),
                    };
                }

                return {
                    ...contentBlock,
                    contentBlocks: [],
                };
            }

            return {
                ...contentBlock,
                contentBlocks: childBlocks?.map((child) => {
                    return getContentBlockWithChildren(child.id);
                }),
            };
        },
        [_contentBlocks],
    );

    const getContentBlock = useCallback(
        (contentBlockId: string) => {
            if (_contentBlocks !== undefined) {
                return _contentBlocks[contentBlockId];
            }
        },
        [_contentBlocks],
    );

    const getPageByRoute = useCallback(
        (route: string) => {
            return Object.values(_contentBlocks).find(
                (contentBlock) =>
                    contentBlock.content_block_type === ContentBlockType.Page &&
                    contentBlock.route.indexOf(route) !== -1,
            );
        },
        [_contentBlocks],
    );

    return {
        getContentBlockWithChildren,
        getContentBlock,
        getPageByRoute,
        rootContentBlocks,
    };
};

export const useGetContentBlocksByRoundId = () => {
    const dispatch = useAppDispatch();

    const _gotContentBlocks = useCallback(
        (contentBlocks: (ContentBlock | ContentBlockShape)[]) =>
            dispatch(gotContentBlocks(contentBlocks)),
        [dispatch],
    );

    return {
        gotContentBlocks: _gotContentBlocks,
    };
};

// export const useSimulationTheme = () => {
//     const _simulationTheme = useAppSelector(
//         (state) => state.contentBlockStore.simulationTheme,
//     );

//     return {
//         simulationTheme: _simulationTheme,
//     };
// };


// export const useGotTopLevelContentBlockLayers = () => {
//     const dispatch = useAppDispatch();

//     const _gotTopLevelContentBlockLayers = useCallback(
//         (topLevelContentBlockLayers) => {
//             dispatch(gotTopLevelContentBlockLayers(topLevelContentBlockLayers));
//         },
//         [dispatch],
//     );

//     return {
//         gotTopLevelContentBlockLayers: _gotTopLevelContentBlockLayers,
//     };
// };

export const useGotContentBlock = () => {
    const dispatch = useAppDispatch();

    const _gotContentBlock = useCallback(
        (contentBlock: ContentBlockShape) => {
            dispatch(gotContentBlock(contentBlock));
            contentBlock?.contentBlocks?.forEach((childContentBlock) => {
                _gotContentBlock(childContentBlock);
            });
        },
        [dispatch],
    );

    return {
        gotContentBlock: _gotContentBlock,
    };
};
